import { ReactElement, useEffect, useRef, useState } from 'react';
import BlockIcon from '@mui/icons-material/Block';
import { Box, Tooltip } from '@mui/material';

const ICON_OFFSET = 43; // Distance from tooltip edge to icon (cursor sits the other side of the icon).

const readPlacement = (el: HTMLElement | null): 'left' | 'right' => {
    const placement = el?.closest('[data-popper-placement]')?.getAttribute('data-popper-placement') ?? '';
    return placement.startsWith('left') ? 'left' : 'right';
};

export const DisabledTooltip = () => {
    const boxRef = useRef<HTMLDivElement>(null);
    const [placement, setPlacement] = useState<'left' | 'right'>('right');

    useEffect(() => {
        const boxEl = boxRef.current;
        const updatePlacement = () => setPlacement(readPlacement(boxRef.current));
        updatePlacement();
        const raf1 = requestAnimationFrame(updatePlacement);
        const raf2 = requestAnimationFrame(updatePlacement);
        const t = setTimeout(() => {
            updatePlacement();
            const popperEl = boxRef.current?.closest('[data-popper-placement]');
            if (popperEl && !popperEl.hasAttribute('data-mutation-observer-disabled-tooltip')) {
                popperEl.setAttribute('data-mutation-observer-disabled-tooltip', '1');
                const observer = new MutationObserver(updatePlacement);
                observer.observe(popperEl, {
                    attributes: true,
                    attributeFilter: ['data-popper-placement'],
                });
                const cleanup = () => {
                    observer.disconnect();
                    popperEl.removeAttribute('data-mutation-observer-disabled-tooltip');
                };
                const cleanupRef = { current: cleanup };
                (popperEl as any).__disabledTooltipCleanup = () => cleanupRef.current();
            }
        }, 50);

        let observer: MutationObserver | null = null;
        const popperEl = boxEl?.closest('[data-popper-placement]');
        if (popperEl) {
            observer = new MutationObserver(updatePlacement);
            observer.observe(popperEl, {
                attributes: true,
                attributeFilter: ['data-popper-placement'],
            });
        }

        window.addEventListener('resize', updatePlacement);
        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            clearTimeout(t);
            observer?.disconnect();
            (boxEl?.closest('[data-popper-placement]') as any)?.__disabledTooltipCleanup?.();
            window.removeEventListener('resize', updatePlacement);
        };
    }, []);

    const isLeft = placement === 'left';

    return (
        <Box
            ref={boxRef}
            data-testid="disabled-tooltip-content"
            role="tooltip"
            sx={{
                display: 'flex',
                flexDirection: isLeft ? 'row-reverse' : 'row',
                alignItems: 'center',
                position: 'relative',
            }}
        >
            <BlockIcon
                sx={{
                    color: 'error.main',
                    fontSize: 16,
                    position: 'absolute',
                    ...(isLeft ? { right: -ICON_OFFSET, left: 'auto' } : { left: -ICON_OFFSET }),
                    top: '50%',
                    transform: 'translateY(-50%)',
                }}
            />
            <span>This cannot be edited</span>
        </Box>
    );
};

type DisabledTooltipProps = {
    disabled: boolean;
    children: ReactElement;
};

export const DisabledTooltipContent = ({ disabled, children }: DisabledTooltipProps) => (
    <Tooltip
        title={disabled ? <DisabledTooltip /> : ''}
        arrow
        followCursor
        PopperProps={{
            popperOptions: {
                // Tweak offset[1]: distance (px) from cursor to tooltip. Larger = more gap (cursor→icon and icon→tooltip).
                modifiers: [{ name: 'offset', options: { offset: [0, 20] } }],
            },
        }}
    >
        {children}
    </Tooltip>
);

export default DisabledTooltipContent;
